import { inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { IBaseTab, ITabDashboard, ITabReport, ReportTabActions, ReportTabSelectors } from './store';

@Injectable({
    providedIn: 'root'
})
export class NavigationTabsService {
    private _store = inject(Store);

    // Emits the click event to switch between open tabs
    public _tabSwitch = signal<{ type: string; id: string }>({ type: '', id: '' });
    // Emits the click on close button when tab is not saved
    public _unsavedTabClosing = signal<{ type: string; id: string }>({ type: '', id: '' });
    // Last closed tab
    public _tabClosed = signal<{ type: string; id: string }>({ type: '', id: '' });
    // Dictionary of contents types to be rendered (Ex: MT_REPORT: ReportComponent)
    // close invalid tab
    public _closeInvalidTab = signal<string>('');
    public _contentTypes = signal<{ [key: string]: any }>({});
    // If it has to be closed after saving
    private _tabCloseOnSave = signal<string[]>([]);

    /**
     * Create a new tab context with initial properties
     * @param params
     * @returns the created tab context stored in the entity store
     */
    public createTab(tab: IBaseTab<ITabDashboard | ITabReport>) {
        this._store.dispatch(
            ReportTabActions.addTabAction({
                tab: tab
            })
        );
    }

    /**
     * Check if the tab with the given id already exists.
     * @param id
     * @returns boolean for tab id existing in current tab list
     */
    public tabExists(id?: string): Observable<boolean> {
        return this._store.select(ReportTabSelectors.getTabInfo).pipe(map(tabInfo => Object.values(tabInfo).some((data: any) => data.id === id)));
    }

    /**
     * Get closed tab id.
     * @returns string for tab id closed in the tab list
     */
    public getClosedTabId(): { type: string; id: string } {
        return this._tabClosed();
    }

    /**
     *
     * @returns the current active tab
     */
    public getActiveTab(): Observable<IBaseTab<ITabReport | ITabDashboard> | null | undefined> {
        return this._store.select(ReportTabSelectors.selectCurrentTab);
    }

    /**
     * Triggers the dispatch for closing a tab and deleting tab data from indexdb
     * @param type tab content identifier
     * @param id tab id
     */
    public closeTab(type: string, id: string) {
        this._store.dispatch(ReportTabActions.closeTabAction({ id: id })); // close tab
        this._tabClosed.set({ type, id });
    }

    /**
     * Triggers the dispatch for navigating and grabs the current selected tab details.
     * Set the tab id tabSwitch signal to allow trigger effects in other components:
     * mt-reporter-page, mediascope-page
     * @param type string to identify the feature
     * @param id tab id
     */
    public switchTab(type: string, id: string): void {
        this._store.dispatch(ReportTabActions.switchTabAction({ id: id }));
        this._tabSwitch.set({ type, id });
    }
    /**
     * Builds object of different types of tab content, assigning a key and component.
     * Content types and associated component can be registered by their respective pages/modules
     * so that the tab component doesn't have to pull in the component itself
     * @param contentType
     */
    public registerContentType(contentType: { [key: string]: any }): void {
        this._contentTypes.set({
            ...this._contentTypes(),
            ...contentType
        });
    }

    public tabCloseOnSave() {
        return this._tabCloseOnSave();
    }

    public addToTabCloseOnSave(tabId: string) {
        if (!this._tabCloseOnSave().includes(tabId)) {
            this._tabCloseOnSave.set([...this.tabCloseOnSave(), tabId]);
        }
    }
    public removeFromTabCloseOnSave(tabId: string) {
        const newTabSet = this._tabCloseOnSave().filter(tid => tid !== tabId);
        this._tabCloseOnSave.set(newTabSet);
    }
}
